import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ColDef, Column, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import {
  assign,
  isArray,
  isEmpty,
  isFunction,
  isObject,
  merge,
  mergeWith
} from 'lodash';

import * as ModelClasses from '../classes/models';
import { GenericObject } from '../interfaces/generic-object';

@Injectable({
  providedIn: 'root'
})
export class GridService {
  constructor(private http: HttpClient) {}

  public getGridColumns(gridType: string | number | symbol, gridName: string) {
    return this.http.get<ColDef<any>[]>(
      `/api/v2/grid/${String(gridType)}/${gridName}/getGridColumns`
    );
  }

  private _handleFieldParts(
    model: keyof typeof ModelClasses,
    fieldParts: string[],
    queryObject: { [key: string]: any } = {}
  ) {
    const ModelClass = ModelClasses[model];

    if (!ModelClass) {
      throw new Error(`Model Class for: ${model} has not been defined.`);
    }

    const fieldPart = fieldParts.shift();
    if (fieldPart) {
      if (ModelClass.isColumn(fieldPart)) {
        if (!isArray(queryObject['attributes'])) {
          assign(queryObject, { attributes: [] });
        }

        const { attributes } = queryObject;

        if (ModelClass.isJSONColumn(fieldPart)) {
          const jsonValueArray = fieldParts.splice(0);
          attributes.push({
            [fieldPart]: {
              ['$jsonExtract']: {
                jsonValue: `$.${jsonValueArray.join('.')}`
              }
            }
          });
        } else if (!attributes.includes(fieldPart)) {
          // Should change attributes to a Set, then transform into an array before sending to backend
          // for querying.
          attributes.push(fieldPart);
        }
      }

      if (ModelClass.isAlias(fieldPart)) {
        if (!isArray(queryObject['aliasedAttributes'])) {
          assign(queryObject, { aliasedAttributes: [] });
        }

        const { aliasedAttributes } = queryObject;
        aliasedAttributes.push(fieldPart);
      }

      if (ModelClass.isAssociation(fieldPart)) {
        const Association = ModelClass.associationMap.get(fieldPart);

        if (Association) {
          const { model } = Association;
          if (!isObject(queryObject[model])) {
            assign(queryObject, { [model]: {} });
          }
          this._handleFieldParts(
            model as keyof typeof ModelClasses,
            fieldParts,
            queryObject[model]
          );
        }
      }
    }
  }

  private _handleFilterParts(
    baseModel: keyof typeof ModelClasses,
    model: keyof typeof ModelClasses,
    filterParts: string[],
    filterValue: any,
    queryObject: { [key: string]: any } = {}
  ) {
    const ModelClass = ModelClasses[model];

    if (!ModelClass) {
      throw new Error(`Model Class for: ${model} has not been defined.`);
    }

    const filterPart = filterParts.shift();
    if (filterPart) {
      if (ModelClass.isAssociation(filterPart)) {
        const Association = ModelClass.associationMap.get(filterPart);
        if (Association) {
          const { model } = Association;
          if (!isObject(queryObject[model])) {
            assign(queryObject, { [model]: {} });
          }

          this._handleFilterParts(
            baseModel,
            model as keyof typeof ModelClasses,
            filterParts,
            filterValue,
            queryObject[model]
          );
        }
      } /* else if (ModelClass.isAlias(filterPart)) {
        // if (!isObject(queryObject['literals'])) {
        //   assign(queryObject, { literals: {} });
        // }
        // const { literals } = queryObject;
        // assign(literals, { [filterPart]: filterValue });
        assign(queryObject, { [filterPart]: filterValue });
      }*/ else {
        // If this is an association, convert it into an inner join when applying filter.
        if (baseModel !== model) {
          if (!isObject(queryObject['options'])) {
            assign(queryObject, { options: {} });
          }
          const { options } = queryObject;
          assign(options, { required: true });
        }

        // Monitor this closely, it could cause unintended side effects. If the queryObject
        // already contains a filter item, move the filter item value to an AND object, and
        // apply the new filter item value.
        if (queryObject[filterPart] !== undefined) {
          const filterPartValue = queryObject[filterPart];
          Object.assign(queryObject, { [filterPart]: { $and: [] } });
          // There could, theoretically, be other values in the filterParamValue if it's an object.
          // Monitor this closely.
          if (typeof filterPartValue === 'object' && filterPartValue.$and) {
            const { $and } = filterPartValue;
            queryObject[filterPart].$and.push(...$and, filterValue);
          } else {
            queryObject[filterPart].$and.push(filterPartValue, filterValue);
          }
        } else {
          Object.assign(queryObject, { [filterPart]: filterValue });
        }
      }
    }
  }

  // When performing column grouping, we need to omit attribute data (except the columns we're grouping on)
  // from our query object to allow the group sql to execute properly.
  private _removeAttributesForGroupingOrAggregation(
    model: keyof typeof ModelClasses,
    queryObject: { [key: string]: any },
    groupOrAggregationFields: Set<string>,
    associationPath: string[] = [],
    { isAggregation = false }
  ) {
    const ModelClass = ModelClasses[model];
    if (!ModelClass) {
      throw new Error(`Model Class for: ${model} has not been defined.`);
    }

    Object.keys(queryObject).forEach((property) => {
      const value = queryObject[property];

      if (
        ['attributes', 'aliasedAttributes'].includes(property) &&
        isArray(value)
      ) {
        // Compose an array of attributes that exist in the groupOrAggregationFields set (if any exist).
        const remainingAttributes: Set<string> = new Set();
        value.forEach((attribute) => {
          if (
            groupOrAggregationFields.has(
              [...associationPath, attribute].join('.')
            ) &&
            !remainingAttributes.has(attribute) &&
            !isAggregation
          ) {
            remainingAttributes.add(attribute);
          }
        });
        Object.assign(queryObject, {
          [property]: Array.from(remainingAttributes)
        });
      }

      if (ModelClass.isAssociation(property)) {
        if (!isArray(queryObject[property]['attributes'])) {
          Object.assign(queryObject[property], { ['attributes']: [] });
        }
        const path = [...associationPath, property];
        this._removeAttributesForGroupingOrAggregation(
          property as keyof typeof ModelClasses,
          value,
          groupOrAggregationFields,
          path,
          { isAggregation }
        );
      }
    });
  }

  private _applyGroupKeyToGroupFieldInQueryObject(
    groupFieldParts: string[],
    groupKey: any,
    queryObject: { [key: string]: any }
  ) {
    const groupFieldPart = groupFieldParts.shift();

    if (groupFieldPart === undefined) {
      throw new Error('Group Field Part is undefined.');
    }

    if (groupFieldParts.length === 0) {
      Object.assign(queryObject, {
        [groupFieldPart]: groupKey !== null ? groupKey : { $is: groupKey }
      });
    } else {
      const associationObject = queryObject[groupFieldPart];
      if (!isObject(queryObject[groupFieldPart])) {
        throw new Error(
          `Query object is missing association for ${groupFieldPart} during group key assignment.`
        );
      }
      this._applyGroupKeyToGroupFieldInQueryObject(
        groupFieldParts,
        groupKey,
        associationObject
      );
    }
  }

  private _handleAggregations(
    model: keyof typeof ModelClasses,
    fieldParts: string[],
    field: string,
    aggregationName: string,
    queryObject: { [key: string]: any }
  ) {
    const ModelClass = ModelClasses[model];

    if (!ModelClass) {
      throw new Error(`Model Class for: ${model} has not been defined.`);
    }

    const fieldPart = fieldParts.shift();

    if (fieldPart === undefined) {
      throw new Error('Field Part is undefined.');
    }

    if (ModelClass.isColumn(fieldPart)) {
      if (!isArray(queryObject['aggregations'])) {
        Object.assign(queryObject, { aggregations: [] });
      }
      const { aggregations } = queryObject;
      aggregations.push([field, aggregationName]);
    }

    if (ModelClass.isAssociation(fieldPart)) {
      if (!isObject(queryObject[fieldPart])) {
        assign(queryObject, { [fieldPart]: {} });
      }
      this._handleAggregations(
        fieldPart as keyof typeof ModelClasses,
        fieldParts,
        field,
        aggregationName,
        queryObject[fieldPart]
      );
    }
  }

  // This is primarily used for count queries. Set empty attributes because the count query sets its own.
  private _setEmptyAttributesForQuery(queryObject: GenericObject) {
    const ObjectKeys = Object.keys(queryObject);
    ObjectKeys.forEach((key) => {
      const queryValue = queryObject[key];

      if (
        ['attributes', 'aliasedAttributes'].includes(key) &&
        Array.isArray(queryValue)
      ) {
        queryValue.splice(0);
      }

      if (key === 'parameterizedAttributes') {
        const parameterizedAttributes = queryObject[key];
        queryObject['parameterizedWhere'] = parameterizedAttributes;
        delete queryObject[key];
      }

      if (
        typeof queryValue === 'object' &&
        !Array.isArray(queryValue) &&
        queryValue !== null
      ) {
        this._setEmptyAttributesForQuery(queryValue);
      }
    });
  }

  // This maps the ag-grid filter type to the backend's operator equivalent.
  // The filterFormatter function applies formatting to the filter value before
  // being sent to the backend for querying.
  static _filterTypeToQueryOperator: {
    [key: string]: {
      operator: string;
      filterFormatter?: (key: string) => string;
    };
  } = {
    contains: {
      operator: '$like',
      filterFormatter: (value: string) => `%${value}%`
    },
    notContains: {
      operator: '$notLike',
      filterFormatter: (value: string) => `%${value}%`
    },
    equals: { operator: '$eq' },
    notEqual: { operator: '$ne' },
    startsWith: {
      operator: '$like',
      filterFormatter: (value: string) => `${value}%`
    },
    endsWith: {
      operator: '$like',
      filterFormatter: (value: string) => `%${value}`
    },
    greaterThan: { operator: '$gt' },
    lessThan: { operator: '$lt' },
    inRange: { operator: '$between' },
    greaterThanOrEqual: { operator: '$gte' },
    lessThanOrEqual: { operator: '$lte' },
    in: { operator: '$in' }
    //blank
    //notBlank
  };

  // This does NOT return an observable. This is used to convert a grid definition object into a
  // query that the backend accepts.
  // gridModel represents which base model/table the grid pulls data from.
  public convertGridDefinitionToQuery(
    gridModel: keyof typeof ModelClasses,
    columns: Column[],
    additional: {
      request: IServerSideGetRowsRequest;
      // uniqueKey is currently used to handle ordering on columns with like values.
      // sub-ordering on a unique key (such as the primary key) will produce consistent
      // ordering results from mysql, which will prevent issues with the grid getting stuck
      // in a reload loop on pagination.
      uniqueKey: string | number;
      loadAllData: boolean;
      loadAllRows: boolean;
      externalFilters?: Map<string, any>;
      queryData?: { [key: string]: any };
      suppressFieldDotNotation?: boolean;
      UniversalSearch: { text: string };
      isCountQuery: boolean;
      preventDistinctCount: boolean;
      omitGroupByOnCount: boolean;
    }
  ): { [key: string]: any } {
    const {
      request,
      uniqueKey,
      loadAllData,
      loadAllRows,
      externalFilters,
      queryData,
      suppressFieldDotNotation,
      UniversalSearch,
      isCountQuery,
      preventDistinctCount,
      omitGroupByOnCount
    } = additional;

    // We're going to mark grid queries as 'raw' for now because grids
    // currently display 'flat' (non-nested) data contained within a record.
    const queryObject: { [key: string]: any } = {
      options: { raw: suppressFieldDotNotation }
    };

    if (isCountQuery) {
      if (!queryObject['options']) {
        Object.assign(queryObject, { options: {} });
      }
      const { options } = queryObject;
      Object.assign(options, { distinct: !preventDistinctCount });
    }

    // This is external data that can be applied to the grid.
    if (queryData) {
      mergeWith(queryObject, queryData, (sourceValue, targetValue) => {
        if (Array.isArray(sourceValue)) {
          const sourceValueSet = new Set(sourceValue);
          targetValue.forEach((v: any) => {
            sourceValueSet.add(v);
          });
          return Array.from(sourceValueSet);
        }
        return;
      });
    }

    // subQuery:false breaks count() — it causes required includes to use LEFT JOIN
    // instead of INNER JOIN, so count returns more rows than findAll, creating an infinite loop.
    if (isCountQuery && queryObject['options']?.['subQuery'] === false) {
      delete queryObject['options']['subQuery'];
    }

    const {
      startRow,
      endRow,
      filterModel,
      sortModel,
      rowGroupCols,
      groupKeys,
      valueCols,
      pivotCols,
      pivotMode
    } = request;

    const { text: rawUniversalSearchText } = UniversalSearch;
    // Normalize whitespace: collapse multiple spaces into '%' wildcards so that
    // searching for a vendor name with extra spaces (e.g. "Acme  Construction")
    // still matches the database value regardless of internal spacing.
    const universalSearchText = rawUniversalSearchText
      ? rawUniversalSearchText.trim().replace(/\s+/g, '%')
      : rawUniversalSearchText;

    const groupFields = new Set<string>();
    const aggregationFields = new Set<string>();

    for (let gc of rowGroupCols) {
      const { field } = gc;
      if (field) {
        groupFields.add(field);
      }
    }

    for (let vc of valueCols) {
      const { field } = vc;
      if (field) {
        aggregationFields.add(field);
      }
    }

    columns.forEach((column) => {
      const colDef: ColDef = column.getColDef();
      const { field, hide } = colDef;

      if (field && (column.isVisible() || loadAllData)) {
        const fieldParts = field.split('.');
        this._handleFieldParts(gridModel, fieldParts, queryObject);
      }

      if (field && universalSearchText && column.isVisible() && !loadAllData) {
        if (!queryObject['associationWhere']) {
          Object.assign(queryObject, {
            associationWhere: { $and: [{ $or: [] }] }
          });
        } else if (!queryObject['associationWhere']['$and']) {
          Object.assign(queryObject['associationWhere'], {
            $and: [{ $or: [] }]
          });
        } else if (
          !queryObject['associationWhere']['$and'][0] ||
          !queryObject['associationWhere']['$and'][0]['$or']
        ) {
          queryObject['associationWhere']['$and'].unshift({ $or: [] });
        }

        const {
          associationWhere: {
            $and: {
              0: { $or }
            }
          }
        } = queryObject;

        if (field.includes('.')) {
          $or.push({
            [`$${field}$`]: { $like: `%${universalSearchText}%` }
          });
        } else {
          $or.push({
            [`$${gridModel}.${field}$`]: {
              $like: `%${universalSearchText}%`
            }
          });
        }
      }
    });

    if (
      !loadAllData &&
      !loadAllRows &&
      startRow !== undefined &&
      endRow !== undefined
    ) {
      if (!isObject(queryObject['options'])) {
        Object.assign(queryObject, { options: {} });
      }
      const { options } = queryObject;
      merge(options, { limit: endRow - startRow, offset: startRow });
    }

    if (isObject(filterModel) && !isEmpty(filterModel) && !loadAllData) {
      Object.keys(filterModel).forEach((property) => {
        const filterObject = filterModel[property as keyof typeof filterModel];

        const { dateFrom, dateTo, filterType, type, filter, values } =
          filterObject;

        const properties = property.split('.');

        const { operator, filterFormatter } =
          GridService._filterTypeToQueryOperator[
            filterType === 'set' ? 'in' : type
          ];

        const value = {};

        if (filter) {
          let formattedFilter =
            typeof filter === 'string' ? filter.trim() : filter;
          Object.assign(value, {
            [operator]: isFunction(filterFormatter)
              ? filterFormatter(formattedFilter)
              : formattedFilter
          });
        }

        if (dateFrom) {
          if (dateTo) {
            Object.assign(value, {
              [operator]: isFunction(filterFormatter)
                ? [filterFormatter(dateFrom), filterFormatter(dateTo)]
                : [dateFrom, dateTo]
            });
          } else {
            if (type === 'equals') {
              const dateFromWithEndOfDay = `${dateFrom.split(' ')[0]} 23:59:59`;
              Object.assign(value, {
                ['$between']: isFunction(filterFormatter)
                  ? [
                      filterFormatter(dateFrom),
                      filterFormatter(dateFromWithEndOfDay)
                    ]
                  : [dateFrom, dateFromWithEndOfDay]
              });
            } else {
              Object.assign(value, {
                [operator]: isFunction(filterFormatter)
                  ? filterFormatter(dateFrom)
                  : dateFrom
              });
            }
          }
        }

        if (values) {
          if (isArray(values)) {
            Object.assign(value, {
              [operator]: isFunction(filterFormatter)
                ? filterFormatter((values as []).join(''))
                : values
            });
          }
        }

        this._handleFilterParts(
          gridModel,
          gridModel,
          properties,
          value,
          queryObject
        );
      });
    }

    if (isArray(sortModel) && sortModel.length > 0) {
      if (!queryObject['order']) {
        Object.assign(queryObject, { order: [] });
      }
      const { order } = queryObject;
      order.unshift(
        ...sortModel.map((sortObject) => {
          const { colId, sort } = sortObject;
          return [...colId.split('.'), sort];
        })
      );

      // Due to the inconsistency of MySql sorting data with 'like' values, we can use
      // the id of a field as an additional sort criteria to guarantee consistency.
      if (order.length > 0) {
        order.push([uniqueKey, 'asc']);
      }
    }

    if (isArray(rowGroupCols) && groupKeys.length === 0) {
      if (!isArray(queryObject['group'])) {
        Object.assign(queryObject, { group: [] });
      }
      const { group } = queryObject;
      rowGroupCols.forEach((groupColumnObject) => {
        const { field } = groupColumnObject;
        group.push(field);
      });
    }

    // Allow users to apply aggregation to grouped columns
    if (valueCols.length > 0) {
      valueCols.forEach((valueCol) => {
        const { field, aggFunc } = valueCol;
        if (field !== undefined && aggFunc !== undefined) {
          const fieldParts = field.split('.');
          this._handleAggregations(
            gridModel,
            fieldParts,
            field,
            aggFunc,
            queryObject
          );
        }
      });
    }

    // Not 100% sure exactly how groupKeys works with multiple column groupings.
    // For now, we'll work with a single column grouping.
    if (rowGroupCols.length > 0) {
      rowGroupCols.forEach((rowGroupCol) => {
        const { field } = rowGroupCol;
        if (field !== undefined) {
          const groupFieldParts = field.split('.');
          this._handleFieldParts(gridModel, groupFieldParts, queryObject);
        }
      });

      if (groupKeys.length > 0) {
        const GroupFieldToKeyMap: Map<string, any> = new Map<string, any>();

        for (let i = 0; i < rowGroupCols.length; i += 1) {
          const rowGroupCol = rowGroupCols[i];
          const groupKey = groupKeys[i];

          if (rowGroupCol && rowGroupCol.field && groupKey !== undefined) {
            GroupFieldToKeyMap.set(rowGroupCol.field, groupKey);
          }
        }

        GroupFieldToKeyMap.forEach((groupKey, groupField) => {
          const groupFieldParts = groupField.split('.');
          this._applyGroupKeyToGroupFieldInQueryObject(
            groupFieldParts,
            groupKey,
            queryObject
          );
        });
      }

      // If we're aggregating on column data and expanding a grouped column,
      // we need to group by the aggregated column(s).
      if (valueCols.length > 0 && groupKeys.length > 0) {
        if (!isArray(queryObject['group'])) {
          Object.assign(queryObject, { group: [] });
        }
        const { group } = queryObject;
        valueCols.forEach((vc) => {
          const { field } = vc;
          if (field !== undefined) {
            group.push(field);
          }
        });
      }
    }

    // Allow pivoting if an aggregation is applied and pivoting is active.
    if (pivotMode && pivotCols.length > 0 && valueCols.length > 0) {
      if (!isArray(queryObject['pivot'])) {
        Object.assign(queryObject, { pivot: [] });
      }
      const { pivot } = queryObject;
      pivotCols.forEach((pc) => {
        const { field } = pc;
        if (field !== undefined) {
          pivot.push(field);
        }
      });
    }

    // This represents filters that need to be applied to the query from outside the grid.
    // Warning: these filters can replace grid-filters!
    if (externalFilters) {
      externalFilters.forEach((value, property) => {
        const properties = property.split('.');
        this._handleFilterParts(
          gridModel,
          gridModel,
          properties,
          value,
          queryObject
        );
      });
    }

    if (groupFields.size > 0 && groupKeys.length === 0) {
      this._removeAttributesForGroupingOrAggregation(
        gridModel,
        queryObject,
        groupFields,
        [],
        { isAggregation: false }
      );
    }

    if (rowGroupCols.length === 0 && valueCols.length > 0) {
      this._removeAttributesForGroupingOrAggregation(
        gridModel,
        queryObject,
        aggregationFields,
        [],
        { isAggregation: true }
      );
    }

    if (isCountQuery) {
      this._setEmptyAttributesForQuery(queryObject);

      // We're going to remove grouping from count queries if omitGroupByOnCount is true.
      // As long as a 'col' is defined in the options for grouping, the count query should
      // return a valid number.
      if (omitGroupByOnCount) {
        const { group } = queryObject;
        if (Array.isArray(group)) {
          group.splice(0);
        }
      }
    }

    // const { options } = queryObject;
    // Object.assign(options, { logging: true });

    // console.log(structuredClone(queryObject));

    return queryObject;
  }

  // public getVendorsGridColumns(gridName)

  /*public getCompaniesCount() {

  }

  public getVendors(VendorId: number) {

  }
  public getVendorsCount() {

  }

  public getUsers(UserId: number) {

  }
  public getUsersCount() {

  }*/
}
